import React, { useState, useEffect, useMemo, useRef } from "react";
import { db } from "../../lib/firebase";
import { collection, getDocs, doc, updateDoc, getDoc } from "firebase/firestore";
import { useNotification } from "../../common/NotificationContext";
import {
  LuPackage,
  LuSearch,
  LuLoader,
  LuCheck,
  LuUndo,
  LuBox,
  LuUsers,
  LuActivity,
  LuX,
  LuBarcode,
  LuFilter
} from "react-icons/lu";
import { InputField, Button } from "@cakrawalajakarta/shared-components";

export const Racepack = () => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { showNotification, showConfirm } = useNotification();
  const searchInputRef = useRef(null);
  const topRef = useRef(null);

  const COLLECTION_NAME = "pendaftaranUltra24";

  useEffect(() => {
    fetchParticipants();
    if (searchInputRef.current) searchInputRef.current.focus();
  }, []);

  const enrichWithAvatars = async (list) => {
    const uids = [...new Set(list.map(p => p.uid).filter(Boolean))];
    if (!uids.length) return list;
    const photoMap = {};
    await Promise.all(
      uids.map(async uid => {
        try {
          const snap = await getDoc(doc(db, "users", uid));
          if (snap.exists()) photoMap[uid] = snap.data().photoURL || snap.data().photoUrl || null;
        } catch {}
      })
    );
    return list.map(p => ({ ...p, photoURL: photoMap[p.uid] || p.photoURL || p.photoUrl || null }));
  };

  const fetchParticipants = async () => {
    try {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const paidList = list.filter(p => p.transaction_status === "settlement");
      const enrichedList = await enrichWithAvatars(paidList);
      setParticipants(enrichedList);
    } catch (error) {
      console.error("Error fetching participants:", error);
      showNotification("error", "Failed to load participants");
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const total = participants.length;
    const collected = participants.filter((p) => p.racepackCollected).length;
    const pending = total - collected;
    return { total, collected, pending };
  }, [participants]);

  const handleScan = () => {
    if (!searchQuery.trim()) return;
    const q = searchQuery.toLowerCase().trim();
    // Prioritaskan kecocokan persis (hasil scan QR / ketik BIB), baru cocok
    // sebagian lewat matchesQuery (nama lengkap / BIB name / anggota relay).
    const found =
      participants.find(p =>
        p.orderId?.toLowerCase() === q ||
        p.nomorIdentitas === q ||
        p.bibNumber?.toString().toLowerCase() === q
      ) || searchIndex.find(p => matchesQuery(p, q));

    if (found) {
      setSelectedParticipant(found);
      setSearchQuery("");
      if (searchInputRef.current) searchInputRef.current.focus();
    } else {
      showNotification("info", "No paid participant found matching that ID");
    }
  };

  // Entri anggota relay memakai id turunan ("<orderId>__memberN") supaya key React
  // unik. Penulisan ke Firestore harus tetap ke dokumen tim yang asli.
  const realDocId = (participant) =>
    participant.isRelayMember ? participant.id.split("__member")[0] : participant.id;

  const handleMarkCollected = async (participant) => {
    setProcessing(true);
    try {
      const timestamp = new Date().toLocaleString('id-ID');
      const docId = realDocId(participant);
      await updateDoc(doc(db, COLLECTION_NAME, docId), {
        racepackCollected: true,
        racepackCollectedAt: timestamp,
      });

      setParticipants(prev => prev.map(p =>
        p.id === docId ? { ...p, racepackCollected: true, racepackCollectedAt: timestamp } : p
      ));
      
      setSelectedParticipant(prev => ({ ...prev, racepackCollected: true, racepackCollectedAt: timestamp }));
      // showNotification("success", `Racepack marked as collected for ${participant.namaLengkap}`);
    } catch (error) {
      console.error("Error marking collected:", error);
      showNotification("error", "Failed to update record");
    } finally {
      setProcessing(false);
    }
  };

  const handleUndoCollection = (participant) => {
    showConfirm(
      "Undo this collection? Data will be reset.",
      () => _doUndoCollection(participant),
      "Undo",
      "Cancel"
    );
  };

  const _doUndoCollection = async (participant) => {
    setProcessing(true);
    try {
      const docId = realDocId(participant);
      await updateDoc(doc(db, COLLECTION_NAME, docId), {
        racepackCollected: false,
        racepackCollectedAt: null,
      });

      setParticipants(prev => prev.map(p =>
        p.id === docId ? { ...p, racepackCollected: false, racepackCollectedAt: null } : p
      ));
      
      setSelectedParticipant(prev => ({ ...prev, racepackCollected: false, racepackCollectedAt: null }));
      // showNotification("info", "Collection undone");
    } catch (error) {
      console.error("Error undoing:", error);
      showNotification("error", "Failed to undo record");
    } finally {
      setProcessing(false);
    }
  };

  // Anggota relay tidak punya dokumen sendiri — mereka tersimpan sebagai array
  // relayMembers di dokumen ketua tim. Supaya panitia bisa mencari lewat nama
  // anggota, tiap anggota dijadikan entri pencarian yang tetap menunjuk ke
  // dokumen tim (racepack dikonfirmasi di dokumen tim itu).
  const searchIndex = useMemo(() => {
    const entries = [];
    participants.forEach((p) => {
      entries.push(p);
      (Array.isArray(p.relayMembers) ? p.relayMembers : []).forEach((m, i) => {
        if (!m?.namaLengkap) return;
        entries.push({
          ...p,
          // id unik hanya untuk key React; konfirmasi tetap pakai p.id
          id: `${p.id}__member${i}`,
          isRelayMember: true,
          memberName: m.namaLengkap,
          memberBibName: m.bibName || m.namaLengkap,
          memberJersey: m.sizeJersey || "",
          memberNomorIdentitas: m.nomorIdentitas || "",
          teamLeaderName: p.namaLengkap,
        });
      });
    });
    return entries;
  }, [participants]);

  // Pencarian mencakup: BIB number, nama lengkap, BIB name, order ID, dan NIK.
  // Untuk anggota relay dipakai data anggotanya, bukan data ketua tim.
  const matchesQuery = (p, q) => {
    if (!q) return true;
    const fields = p.isRelayMember
      ? [p.memberName, p.memberBibName, p.memberNomorIdentitas]
      : [p.namaLengkap, p.bibName, p.nomorIdentitas, p.email];
    return (
      fields.some((f) => f?.toString().toLowerCase().includes(q)) ||
      p.bibNumber?.toString().toLowerCase().includes(q) ||
      p.orderId?.toLowerCase().includes(q)
    );
  };

  const filteredList = useMemo(() => {
    // Saat ada kata kunci, cari juga di anggota relay supaya hasil tabel sama
    // dengan hasil kolom scan. Tanpa kata kunci cukup tampilkan peserta utama.
    let filtered = participants;

    if (listSearch) {
        const q = listSearch.toLowerCase().trim();
        filtered = searchIndex.filter(p => matchesQuery(p, q));
    }

    if (statusFilter !== "all") {
        if (statusFilter === "collected") {
            filtered = filtered.filter(p => p.racepackCollected);
        } else if (statusFilter === "awaiting") {
            filtered = filtered.filter(p => !p.racepackCollected);
        }
    }

    return filtered;
  }, [participants, listSearch, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LuLoader className="animate-spin text-4xl text-slate-800" />
      </div>
    );
  }

  return (
    <>
      <div ref={topRef} />
      <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuPackage size={120} />
         </div>
         <div className="relative z-10 flex flex-col md:flex-row justify-between items-start gap-4">
            <div>
               <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                  <LuActivity size={14} /> RPC Management
               </p>
               <h1 className="text-2xl md:text-3xl font-bold mb-1">Racepack Collection</h1>
               <p className="text-slate-500 text-sm max-w-xl">
                  Search participants and mark their racepack as collected.
               </p>
            </div>
            <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-xs font-semibold border border-green-100">
               <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
               USB SCANNER READY
            </div>
         </div>
      </div>

      {/* Split Pane: Scan and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-col-reverse">
        
        {/* Left Card: Scanner */}
        <div className="lg:col-span-5 bg-white rounded-xl border border-slate-200 p-6 flex flex-col items-center justify-center min-h-[250px]">
           <div className="mb-6">
              <LuBarcode size={60} className="text-slate-300" />
           </div>
           <h2 className="text-xl font-bold text-slate-800 mb-2 text-center">Scan E-Ticket</h2>
           <p className="text-slate-400 text-center text-sm mb-6">Focus input and scan QR Code or type ID</p>
           
           <div className="w-full max-w-sm relative">
             <InputField 
                ref={searchInputRef}
                name="scan"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleScan()}
                placeholder="Scan QR or type name/ID..."
                startIcon={<LuSearch className="text-slate-400" />}
                labelClassName="sr-only"
                autoComplete="off"
             />
             
             {/* Suggestions Dropdown */}
             {searchQuery.length >= 2 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden max-h-[300px] overflow-y-auto custom-scrollbar">
                   {searchIndex
                      .filter(p => matchesQuery(p, searchQuery.toLowerCase()))
                      .slice(0, 5)
                      .map((p) => (
                         <div
                            key={p.id}
                            onClick={() => {
                               setSelectedParticipant(p);
                               setSearchQuery("");
                            }}
                            className="p-3 border-b border-slate-50 last:border-none hover:bg-slate-50 cursor-pointer flex justify-between items-center transition-colors"
                         >
                            <div>
                               <p className="font-bold text-slate-800 text-sm leading-tight">
                                  {p.isRelayMember ? p.memberName : p.namaLengkap}
                               </p>
                               <p className="text-[10px] text-slate-400 font-medium uppercase mt-0.5">
                                  {(p.isRelayMember ? p.memberBibName : p.bibName) &&
                                     `${p.isRelayMember ? p.memberBibName : p.bibName} • `}
                                  {p.kategori}
                                  {p.isRelayMember && ` • TIM ${p.teamLeaderName}`}
                               </p>
                            </div>
                            <div className="text-right">
                               <p className="font-bold text-slate-800 text-sm tracking-tight">{p.bibNumber || "-"}</p>
                               <p className="text-[10px] text-slate-400 font-bold uppercase">
                                  {(p.isRelayMember ? p.memberJersey : p.sizeJersey) || "-"}
                               </p>
                            </div>
                         </div>
                      ))}
                   {searchIndex.filter(p => matchesQuery(p, searchQuery.toLowerCase())).length === 0 && (
                      <div className="p-4 text-center text-xs text-slate-400 italic">No matching participants</div>
                   )}
                </div>
             )}
             
             <p className="text-[10px] text-slate-300 mt-2 text-center uppercase tracking-widest font-medium">Auto-detects REG-ID, URL, or Name</p>
           </div>
        </div>

        {/* Right Card: Details */}
        <div className="lg:col-span-7 bg-white rounded-xl border border-slate-200 overflow-hidden min-h-[250px]">
           {!selectedParticipant ? (
               <div className="h-full flex flex-col items-center justify-center p-12 text-center opacity-40">
                  <LuBox size={60} className="text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-500">No Participant Selected</h3>
                  <p className="text-slate-400 text-sm">Scan or search to view details</p>
               </div>
           ) : (
               <div className="h-full flex flex-col">
                  {/* Status Indicator */}
                  {selectedParticipant.racepackCollected ? (
                      <div className="bg-green-50 border-b border-green-100 p-3 flex items-center gap-3 text-green-700">
                         <LuCheck className="text-green-500" />
                         <div>
                            <p className="font-bold text-sm uppercase">Already Collected</p>
                            <p className="text-[10px] opacity-70">{selectedParticipant.racepackCollectedAt}</p>
                         </div>
                      </div>
                  ) : (
                      <div className="bg-slate-50 border-b border-slate-100 p-3 flex items-center gap-3 text-slate-500">
                         <LuPackage />
                         <p className="font-bold text-sm uppercase tracking-wide">Ready for Pickup</p>
                      </div>
                  )}

                  {/* Content */}
                  <div className="p-4 flex-1">
                     <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                        <div className="col-span-2 md:col-span-1 border-r border-slate-100 pr-4">
                           <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">
                              {selectedParticipant.isRelayMember ? "Relay Member" : "Participant"}
                           </p>
                           <h3 className="text-lg font-bold text-slate-800 leading-tight mb-0.5">
                              {selectedParticipant.isRelayMember
                                 ? selectedParticipant.memberName
                                 : selectedParticipant.namaLengkap}
                           </h3>
                           <p className="text-slate-500 text-xs">
                              {(selectedParticipant.isRelayMember
                                 ? selectedParticipant.memberNomorIdentitas
                                 : selectedParticipant.nomorIdentitas) || "-"}
                           </p>

                           {/* Racepack relay dikonfirmasi di dokumen tim, bukan per anggota. */}
                           {selectedParticipant.isRelayMember && (
                              <p className="mt-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
                                 Racepack 1 paket per tim — atas nama {selectedParticipant.teamLeaderName}
                              </p>
                           )}

                           <div className="mt-4 space-y-3">
                              <div>
                                 <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Category</p>
                                 <p className="text-sm font-medium text-slate-700">{selectedParticipant.kategori || "-"}</p>
                              </div>
                              <div>
                                 <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Gender</p>
                                 <p className="text-sm font-medium text-slate-700 uppercase">{selectedParticipant.gender || "-"}</p>
                              </div>
                           </div>
                        </div>

                        <div className="col-span-2 md:col-span-1 flex flex-col justify-between">
                           <div>
                              <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">Jersey Size</p>
                              <div className="text-5xl font-bold text-slate-800">
                                 {(selectedParticipant.isRelayMember
                                    ? selectedParticipant.memberJersey
                                    : selectedParticipant.sizeJersey) || "?"}
                              </div>
                           </div>
                           
                           <div className="mt-4">
                              <p className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-0.5">BIB Number</p>
                              <p className="text-3xl font-bold text-slate-800">{selectedParticipant.bibNumber || "PENDING"}</p>
                           </div>
                        </div>
                     </div>

                     {/* Actions */}
                     <div className="flex gap-4 mt-4 pt-4 border-t border-slate-50">
                        <Button variant="secondary" onClick={() => setSelectedParticipant(null)}>
                           Cancel
                        </Button>
                        
                        {selectedParticipant.racepackCollected ? (
                           <Button variant="danger-outline" onClick={() => handleUndoCollection(selectedParticipant)} disabled={processing} className="flex-1 uppercase tracking-widest">
                              <LuUndo /> Undo
                           </Button>
                        ) : (
                           <Button variant="none" onClick={() => handleMarkCollected(selectedParticipant)} disabled={processing} className="flex-1 bg-slate-800 hover:bg-slate-900 text-white uppercase tracking-widest">
                              {processing ? <><LuLoader className="animate-spin text-xs" /> Processing</> : "Confirm"}
                           </Button>
                        )}
                     </div>
                  </div>
               </div>
           )}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 w-full">
         <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
               <LuUsers size={20} />
            </div>
            <div>
               <p className="text-slate-500 text-sm font-medium mb-1">Total Paid</p>
               <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.total}</h3>
               <p className="text-sm text-slate-400 mt-1">Paid participants</p>
            </div>
         </div>
         <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
               <LuCheck size={20} />
            </div>
            <div>
               <p className="text-slate-500 text-sm font-medium mb-1">Collected</p>
               <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.collected}</h3>
               <p className="text-sm text-slate-400 mt-1">Packets picked up</p>
            </div>
         </div>
         <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
               <LuBox size={20} />
            </div>
            <div>
               <p className="text-slate-500 text-sm font-medium mb-1">Pending</p>
               <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.pending}</h3>
               <p className="text-sm text-slate-400 mt-1">Awaiting collection</p>
            </div>
         </div>
      </div>

      {/* Filters Area */}
      <div className="bg-white rounded-xl p-4 border">
        <div className="flex flex-col md:flex-row gap-4">
           {/* Search */}
           <div className="flex-1">
              <InputField 
                 name="listSearch"
                 value={listSearch}
                 onChange={(e) => setListSearch(e.target.value)}
                 placeholder="Search Name, Email, or BIB..."
                 startIcon={<LuSearch className="text-slate-400" />}
                 labelClassName="sr-only"
              />
           </div>

           {/* Status Filter */}
           <div className="min-w-[200px]">
              <InputField 
                 type="select"
                 name="statusFilter"
                 value={statusFilter}
                 onChange={(e) => setStatusFilter(e.target.value)}
                 options={[
                    { value: "all", label: "All Status" },
                    { value: "collected", label: "Collected" },
                    { value: "awaiting", label: "Awaiting" }
                 ]}
                 startIcon={<LuFilter className="text-slate-400" />}
                 defaultOptionLabel="Filter Status"
                 labelClassName="sr-only"
              />
           </div>
        </div>
      </div>

      {/* Participant Card List */}
      <div className="flex flex-col gap-2">
        {filteredList.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-12 text-center text-slate-400 italic text-sm">
            No paid participants found.
          </div>
        ) : filteredList.map((p) => (
          <div
            key={p.id}
            className="bg-white rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => { setSelectedParticipant(p); topRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
          >
            <div className="grid grid-cols-12 gap-4 items-center">
              {/* Avatar + Name */}
              <div className="col-span-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-lime-100 flex items-center justify-center text-slate-700 font-bold flex-shrink-0 overflow-hidden">
                  {(p.photoURL || p.photoUrl) ? (
                    <img src={p.photoURL || p.photoUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                  ) : null}
                  <span style={{ display: (p.photoURL || p.photoUrl) ? 'none' : 'flex' }} className="w-full h-full items-center justify-center">
                    {((p.isRelayMember ? p.memberName : p.namaLengkap) || p.teamName || "?").charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">
                    {(p.isRelayMember ? p.memberName : p.namaLengkap) || p.teamName || "-"}
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono truncate">
                    {p.isRelayMember ? `ANGGOTA TIM ${p.teamLeaderName}` : p.orderId}
                  </p>
                </div>
              </div>

              {/* Category */}
              <div className="col-span-3">
                <p className="text-sm font-medium text-slate-700 truncate">{p.kategori || "-"}</p>
              </div>

              {/* BIB / Jersey */}
              <div className="col-span-2 text-center">
                <p className="text-[10px] text-slate-400 font-bold uppercase mb-0.5">BIB / Jersey</p>
                <p className="text-sm font-semibold text-slate-800">
                  {p.bibNumber || "PENDING"} / {(p.isRelayMember ? p.memberJersey : p.sizeJersey) || "-"}
                </p>
              </div>

              {/* Status */}
              <div className="col-span-2 flex justify-center">
                {p.racepackCollected ? (
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                    <LuCheck size={10} /> Collected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                    Awaiting
                  </span>
                )}
              </div>

              {/* Action */}
              <div className="col-span-1 flex justify-end">
                <button className="text-[10px] font-bold uppercase text-slate-400 hover:text-slate-800 transition-colors">Select</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </>
);
};
